// Duff (daf) tap feedback for the onboarding quiz — a WebAudio-synthesized
// frame-drum hit plus haptics. Synthesized, not sampled: zero asset bytes,
// no licensing, and it plays instantly from inside a tap handler.
//
// A daf hit is a low membrane thump (sine with a fast pitch drop) plus a
// very short filtered-noise "slap" of the skin. "correct" plays the classic
// dum-tak pair; "wrong" plays one soft muted dum — feedback, never a
// fail buzzer. Everything is try/catch'd: feedback must never break a tap.
// Vibration is Android-only (iOS has no Vibration API) and no-ops elsewhere.

let ctx = null;

function ensureCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// One drum hit at `when` (ctx time). tone: fundamental Hz; slap: noise level.
function hit(ac, when, { tone = 85, drop = 1.6, gain = 0.5, decay = 0.22, slap = 0.12 }) {
  const out = ac.createGain();
  out.gain.setValueAtTime(gain, when);
  out.gain.exponentialRampToValueAtTime(0.001, when + decay);
  out.connect(ac.destination);

  // Membrane: sine with a quick pitch drop into the fundamental.
  const osc = ac.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(tone * drop, when);
  osc.frequency.exponentialRampToValueAtTime(tone, when + 0.06);
  osc.connect(out);
  osc.start(when);
  osc.stop(when + decay);

  // Skin slap: a few ms of band-passed noise.
  if (slap > 0) {
    const len = Math.floor(ac.sampleRate * 0.03);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const noise = ac.createBufferSource();
    noise.buffer = buf;
    const bp = ac.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 2400;
    bp.Q.value = 0.8;
    const ng = ac.createGain();
    ng.gain.setValueAtTime(slap, when);
    noise.connect(bp).connect(ng).connect(out);
    noise.start(when);
  }
}

/**
 * Play duff feedback for a quiz tap. kind: "correct" | "wrong".
 * Call from inside the tap handler (user gesture) so audio is allowed.
 */
export function duffFeedback(kind) {
  try {
    const ac = ensureCtx();
    if (ac) {
      const now = ac.currentTime;
      if (kind === "correct") {
        hit(ac, now, { tone: 85, gain: 0.4, decay: 0.22, slap: 0.1 }); // dum
        hit(ac, now + 0.13, { tone: 190, drop: 1.3, gain: 0.28, decay: 0.12, slap: 0.16 }); // tak
      } else {
        hit(ac, now, { tone: 70, drop: 1.4, gain: 0.3, decay: 0.28, slap: 0.04 }); // soft dum
      }
    }
  } catch {
    // Audio feedback is best-effort.
  }
  try {
    navigator.vibrate?.(kind === "correct" ? 20 : [25, 50, 25]);
  } catch {
    // Haptics too.
  }
}
